import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { firstValueFrom, of, pipe } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationService, CustomerService, UserService } from '../services';
import { initialUserState, UserState } from '../models/state/user.state';
import { PageRequest, User, UserCreateUpdateRequest } from '../models';
import { Role } from '../enums';
import { SelectedPartnerStore } from './selected-partner.store';
import { HttpErrorResponse } from '@angular/common/http';

export const PartnerUserStore = signalStore(
  { providedIn: 'root' },
  withProps(() => {
    return { partner: inject(SelectedPartnerStore)?.selectedPartner };
  }),
  withState<UserState>(initialUserState),
  withMethods(
    (
      store,
      applicationService = inject(ApplicationService),
      userService = inject(UserService),
      customerService = inject(CustomerService),
      toastrService = inject(ToastrService),
      translateService = inject(TranslateService),
    ) => {
      const loadUsers = rxMethod<PageRequest & { role?: Role }>(
        pipe(
          map((_pageRequest) => {
            const pageRequest = {
              ..._pageRequest,
              page: 0,
              size: applicationService.itemsPerPage(),
            };

            patchState(store, { loading: true, pageRequest });

            return pageRequest;
          }),
          switchMap((pageRequest) =>
            userService.getPartnerUsers(store.partner()?.id, pageRequest).pipe(
              tap((users) => patchState(store, { users })),
              catchError(() => of()),
              finalize(() => patchState(store, { loading: false })),
            ),
          ),
        ),
      );
      const loadMoreUsers = rxMethod<{ role?: Role }>(
        pipe(
          map(({ role }) => {
            const { page, ...restOfPageRequest } = store.pageRequest();
            const pageRequest = { ...restOfPageRequest, page: (page ?? 0) + 1, role };
            patchState(store, { pageRequest });
            return pageRequest;
          }),
          concatMap((pageRequest) =>
            userService
              .getPartnerUsers(store.partner()?.id, pageRequest)
              .pipe(tap((users) => patchState(store, { users: [...store.users(), ...users] }))),
          ),
        ),
      );
      const loadUsersMetrics = rxMethod<{ role?: Role }>(
        pipe(
          tap(() => patchState(store, { metricsLoading: true })),
          switchMap(({ role }) =>
            userService.getPartnerUsersStatistics(store.partner()?.id, role).pipe(
              tap((metrics) => patchState(store, { metrics })),
              catchError(() => of()), // Prevent rxMethod from crashing
              finalize(() => patchState(store, { metricsLoading: false })),
            ),
          ),
        ),
      );

      const deleteUser = rxMethod<User>(
        pipe(
          switchMap((user) =>
            userService.deletePartnerUser(user.id, store.partner()?.id).pipe(
              tap(() => {
                patchState(store, {
                  users: [...store.users().filter((_user) => _user.id !== user.id)],
                });
                loadUsersMetrics({ role: user.role });
                toastrService.success(translateService.instant('users.alerts.deleteSuccess'));
              }),
              catchError((error) => {
                toastrService.error(
                  translateService.instant(
                    error.status === 403 ? 'common.alerts.permissionDenied' : 'common.alerts.genericError',
                  ),
                );
                return of();
              }),
            ),
          ),
        ),
      );

      const loadAllCustomers = rxMethod<void>(
        switchMap(() =>
          customerService.getAllPartnerCustomersLight(store.partner()?.id).pipe(
            tap((customers) => patchState(store, { customers })),
            catchError(() => of()), // Prevent rxMethod from crashing
          ),
        ),
      );
      const resetUsers = () => {
        patchState(store, {
          users: [],
          pageRequest: {
            page: 0,
            size: applicationService.itemsPerPage(),
            sort: undefined,
            searchText: '',
          },
        });
      };

      return {
        loadUsers,
        resetUsers,
        loadMoreUsers,
        loadUsersMetrics,
        deleteUser,
        loadAllCustomers,

        async createCustomerUser(userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });

          try {
            const createdUser = await firstValueFrom(userService.createUserAsUser(userData));

            patchState(store, { users: [...store.users(), createdUser] });
            loadUsersMetrics({ role: userData.role });
            toastrService.success(translateService.instant('users.alerts.createSuccess'));
          } catch (error: HttpErrorResponse | any) {
            let errorMessage = 'common.alerts.genericError';
            if (error.status === 409) {
              errorMessage = 'users.alerts.emailAlreadyUsedError';
            } else if (error.status === 403) {
              errorMessage = 'common.alerts.permissionDenied';
            } else if (error.error.detail.startsWith('SSO users')) {
              errorMessage = 'users.alerts.ssoUsersError';
            }

            toastrService.error(translateService.instant(errorMessage));
          } finally {
            patchState(store, { saving: false });
          }
        },

        async createPartnerUser(userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });
          try {
            const createdUser = await firstValueFrom(
              userService.createUserAsPartner({ ...userData, partnerId: store.partner()?.id }),
            );

            patchState(store, { users: [...store.users(), createdUser] });
            loadUsersMetrics({ role: userData.role });
            toastrService.success(translateService.instant('users.alerts.createSuccess'));
          } catch (error: HttpErrorResponse | any) {
            let errorMessage = 'common.alerts.genericError';
            if (error.status === 409) {
              errorMessage = 'users.alerts.emailAlreadyUsedError';
            } else if (error.status === 403) {
              errorMessage = 'common.alerts.permissionDenied';
            } else if (error.error.detail.startsWith('SSO users')) {
              errorMessage = 'users.alerts.ssoUsersError';
            }

            toastrService.error(translateService.instant(errorMessage));
          } finally {
            patchState(store, { saving: false });
          }
        },

        async updateUser(id: number, userData: UserCreateUpdateRequest): Promise<void> {
          patchState(store, { saving: true });

          try {
            const updatedUser = await firstValueFrom(
              userService.updateUserAsPartner(id, { ...userData, partnerId: store.partner()?.id }),
            );

            patchState(store, {
              users: [...store.users().map((u) => (u.id === id ? { ...u, ...updatedUser } : u))],
            });
            loadUsersMetrics({ role: userData.role });
            toastrService.success(translateService.instant('users.alerts.updateSuccess'));
          } catch (error: HttpErrorResponse | any) {
            toastrService.error(
              translateService.instant(
                error.status === 409
                  ? 'users.alerts.emailAlreadyUsedError'
                  : error.status === 403
                    ? 'common.alerts.permissionDenied'
                    : 'common.alerts.genericError',
              ),
            );
          } finally {
            patchState(store, { saving: false });
          }
        },
      };
    },
  ),
);
